import React from "react";
import { NavLink, Outlet, useLocation, useParams } from "react-router-dom";
import { LayoutDashboard, AppWindow, BookOpen, Plus } from "lucide-react";
import { useApp } from "../App";
import { DOCS_URL } from "../constants";

function NavItem({ icon: Icon, label, to, end }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-all duration-200 group ${
          isActive ? "bg-grigora-50 text-grigora-700 font-medium" : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon size={18} className={`transition-colors ${isActive ? "text-grigora-600" : "text-slate-400 group-hover:text-slate-600"}`} />
          <span className="text-sm">{label}</span>
        </>
      )}
    </NavLink>
  );
}

function Breadcrumb() {
  const { pathname } = useLocation();
  const { appId } = useParams();
  const path = pathname;

  if (path === "/") return <span className="font-medium text-slate-900">Dashboard</span>;
  if (path === "/apps") return <span className="font-medium text-slate-900">My Apps</span>;
  if (path === "/apps/new") return <span className="font-medium text-slate-900">Create App</span>;
  if (path.startsWith("/apps/") && appId) return <span className="font-medium text-slate-900">App Details</span>;
  return null;
}

export function Layout() {
  const { user } = useApp();
  const initials = user
    ? (user.given_name || user.email || "").substring(0, 1).toUpperCase() + (user.family_name || "").substring(0, 1).toUpperCase()
    : "U";
  const displayName = user ? [user.given_name, user.family_name].filter(Boolean).join(" ") || user.email || "Developer" : "Developer";

  return (
    <div className="flex h-screen bg-slate-50 text-slate-900 overflow-hidden font-sans">
      <aside className="w-64 border-r border-slate-200 flex flex-col bg-white flex-shrink-0 z-20 shadow-[4px_0_24px_-12px_rgba(0,0,0,0.1)]">
        <div className="p-6">
          <div className="flex items-center space-x-3">
            <img src={`${process.env.PUBLIC_URL || ""}/logo512.png`} alt="Grigora" className="w-10 h-10 object-contain flex-shrink-0" />
            <div>
              <h1 className="font-bold text-slate-900 tracking-tight text-lg leading-tight">Grigora</h1>
              <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">Dev Portal</p>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-3 mb-3 mt-4">Platform</div>
          <NavItem icon={LayoutDashboard} label="Dashboard" to="/" end />
          <NavItem icon={AppWindow} label="My Apps" to="/apps" end={false} />
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-3 mb-3" style={{ marginTop: "15px", marginBottom: "12px" }}>Resources</div>
          <a
            href={DOCS_URL}
            className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-all duration-200 group text-slate-500 hover:bg-slate-50 hover:text-slate-900"
          >
            <BookOpen size={18} className="text-slate-400 group-hover:text-slate-600" />
            <span className="text-sm">Documentation</span>
          </a>
        </nav>
        <div className="p-4 border-t border-slate-100">
          <div className="flex items-center p-2 rounded-lg">
            <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-grigora-100 to-grigora-200 border border-slate-200 flex items-center justify-center text-xs font-bold text-grigora-700">
              {initials || "U"}
            </div>
            <div className="ml-3 flex-1 min-w-0">
              <p className="text-xs font-semibold text-slate-900 truncate">{displayName}</p>
              <p className="text-[10px] text-slate-500 truncate">{user?.email || ""}</p>
            </div>
          </div>
        </div>
      </aside>
      <main className="flex-1 flex flex-col overflow-hidden relative">
        <header className="h-16 border-b border-slate-200 flex items-center justify-between px-8 bg-white/80 backdrop-blur-xl z-10 sticky top-0">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-sm text-slate-500">
              <NavLink to="/" className="hover:text-slate-800 transition-colors">Home</NavLink>
              <span className="text-slate-300">/</span>
              <Breadcrumb />
            </div>
          </div>
          <div className="flex items-center space-x-6">
            <NavLink
              to="/apps/new"
              className="bg-grigora-500 hover:bg-grigora-600 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center space-x-2 transition-all shadow-lg shadow-grigora-500/20"
            >
              <Plus size={16} />
              <span>New App</span>
            </NavLink>
          </div>
        </header>
        <div className="flex-1 min-h-0 overflow-y-auto bg-slate-50">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
